/**
 * OnboardingGate
 * Renders the wizard as a fixed full-screen overlay when:
 *   1. The user is an administrator, AND
 *   2. The user context API returned no companies on initial load (auto-open), OR
 *   3. The wizard has been manually re-opened (isWizardOpen === true)
 *
 * Sits above everything else. Does not affect routing or AppLayout.
 */

import { useEffect } from 'react';
import { useOnboarding } from '@/contexts/onboarding-context';
import { useUserContext } from '@/hooks/useUserContext';
import { OnboardingWizard } from './onboarding-wizard';

export function OnboardingGate() {
  const { hasDismissedOnboarding, hasCompletedOnboarding, isWizardOpen, savedStep, openWizard } = useOnboarding();
  const { data: userContext, isLoading } = useUserContext();

  const isAdmin = userContext?.role?.toLowerCase() === 'administrator';
  const hasNoCompanies = !userContext?.companies || userContext.companies.length === 0;

  // Auto-open once when the user context first loads and conditions are met.
  // Using isWizardOpen as the sole visibility gate prevents the wizard from
  // closing mid-flow when a company is created and userContext re-fetches.
  useEffect(() => {
    if (!isLoading && isAdmin && hasNoCompanies && !hasDismissedOnboarding && !hasCompletedOnboarding) {
      openWizard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Don't render until the initial load is done — but once the wizard is open, keep it
  // visible even if a background refetch temporarily sets isLoading back to true (e.g.
  // after company creation causes a query-key change in useUserContext).
  if (isLoading && !isWizardOpen) return null;
  if (!isAdmin) return null;
  if (!isWizardOpen) return null;

  const firstName = userContext?.firstName ?? '';
  const lastName = userContext?.lastName ?? '';
  const userName = [firstName, lastName].filter(Boolean).join(' ') || userContext?.email || '';

  const existingCompanyCount = userContext?.companies?.length ?? 0;

  return <OnboardingWizard userName={userName} initialCompanyCount={existingCompanyCount} initialStep={savedStep} />;
}
